import { datadogLogs } from "@datadog/browser-logs";
import { getBuildVersion } from "@frontend/lib/app-version";
import {
  type EhrLocationIdSource,
  type KioskConfigState,
  type PracticeIdSource,
  outcomeForKioskConfigState,
} from "./kiosk-config-state";
import { getKioskIdentity } from "./kiosk-identity";

/**
 * Fleet-wide launch-config telemetry, emitted once per kiosk load.
 *
 * The `notConfigured` error log only fires when a kiosk is BLOCKED, so before
 * this event a healthy tablet was indistinguishable from one that never
 * launched. That makes a rollout unmeasurable: you get a count of failures with
 * no denominator. This event fires on EVERY load — healthy, degraded, and
 * blocked alike — so `configState` can be grouped to get the whole fleet's
 * distribution, and `buildVersion` shows how far the rollout has propagated.
 *
 * Emitted at INFO level on purpose: `kiosk_not_configured` remains the single
 * alertable error signal. This one is for dashboards and triage, and keeping it
 * at one level makes group-by counts clean.
 *
 * Non-PII only — practice/location/kiosk identifiers and the launch URL's
 * path+query, which on the kiosk carries config ids (practiceId, stateId,
 * terminalId), never patient data.
 *
 * The devtool's Health tab can also manually re-fire this event on-site
 * (tagged `triggerSource: "manual"`) for correlating a specific troubleshooting
 * click with the Datadog stream.
 */

/**
 * Query keys whose VALUES are safe to log — opaque configuration identifiers,
 * never patient data. Matched case-insensitively on purpose: a misspelled
 * config key (`?Practiceid=`) is exactly the failure this field exists to
 * diagnose, and its value is still just a practice id.
 *
 * Everything else keeps its key but loses its value. `state` is the reason this
 * allowlist exists: the renderer can serialize the ENTIRE machine context into
 * `?state=<base64>` (see state-serializer `copyStateToClipboard`), and the
 * provider never dissolves that param. Mid-flow that context holds patient
 * identity and intake answers, so copying the raw query string would ship PII
 * to Datadog and break this event's non-PII contract.
 */
const LOGGABLE_QUERY_KEYS = new Set([
  "practiceid",
  "ehrlocationid",
  "qrmode",
  "appointmentid",
  "terminalid",
  "kioskname",
  "stateid",
]);

/**
 * Build a loggable launch URL: known config params keep their values, anything
 * else is reduced to `key=<redacted>`.
 *
 * Keeping unknown KEY names is deliberate — "a param called `state` was
 * present" is useful diagnostic signal, while its value never can be.
 */
export function redactLaunchUrl(pathname: string, search: string): string {
  if (!search) return pathname;

  const redacted = Array.from(new URLSearchParams(search).entries()).map(
    ([key, value]) =>
      LOGGABLE_QUERY_KEYS.has(key.toLowerCase())
        ? `${key}=${value}`
        : `${key}=<redacted>`,
  );

  return redacted.length > 0 ? `${pathname}?${redacted.join("&")}` : pathname;
}

export interface LogKioskConfigStateInput {
  configState: KioskConfigState;
  practiceIdSource: PracticeIdSource;
  ehrLocationIdSource: EhrLocationIdSource;
  practiceId: string | undefined;
  ehrLocationId: string | undefined;
  /** `undefined` when the practice list never loaded, so we genuinely can't tell. */
  practiceFoundInOrg: boolean | undefined;
  practiceFetchFailed: boolean;
  /**
   * Whether this load restored a persisted session via `?stateId=`. The
   * `notConfigured` fail-safe is bypassed on that path (resolveState does not
   * re-run `always` guards), so a blocked-looking config that still reached the
   * flow is identifiable by this flag.
   */
  restoredFromStateId: boolean;
  /**
   * Who fired this event. Defaults to `"automatic"` (the once-per-load emit in
   * `kiosk-provider.tsx`); the devtool's Health tab passes `"manual"` when
   * on-site staff re-fires it to correlate a click with the Datadog stream.
   */
  triggerSource?: "automatic" | "manual";
}

export function logKioskConfigState(input: LogKioskConfigStateInput): void {
  const { kioskId, kioskName, kioskLocation } = getKioskIdentity();
  const shortKioskId = kioskId.slice(0, 8);
  const outcome = outcomeForKioskConfigState(input.configState);
  const triggerSource = input.triggerSource ?? "automatic";

  // The URL the tablet actually booted with — the fastest way to spot a
  // malformed launch param (a `?Practiceid=` casing typo, a missing `/`).
  // Redacted: see redactLaunchUrl.
  const launchUrl =
    typeof window !== "undefined"
      ? redactLaunchUrl(window.location.pathname, window.location.search)
      : null;

  datadogLogs.logger.info(
    `Kiosk config resolved: ${input.configState} (practice ${input.practiceId ?? "none"})`,
    {
      context: {
        action: "kiosk_config_resolved",
        metadata: {
          configState: input.configState,
          configOutcome: outcome,
          practiceIdSource: input.practiceIdSource,
          ehrLocationIdSource: input.ehrLocationIdSource,
          practiceId: input.practiceId ?? null,
          ehrLocationId: input.ehrLocationId ?? null,
          practiceFoundInOrg: input.practiceFoundInOrg ?? null,
          practiceFetchFailed: input.practiceFetchFailed,
          restoredFromStateId: input.restoredFromStateId,
          triggerSource,
          kioskId,
          shortKioskId,
          kioskName: kioskName ?? null,
          kioskLocation: kioskLocation ?? null,
          launchUrl,
          // Lets the rollout be tracked by build: what share of the fleet has
          // actually picked up the new bundle.
          buildVersion: getBuildVersion(),
          tags: ["KIOSK", "CONFIG", "ROLLOUT"],
        },
      },
    },
  );
}
